import { transformJSONFromOld } from 'api/custom-section/transformer';
import { HttpClient } from 'api/http-client';
import {
  CreatePageTemplatePayload,
  PageType,
  SourceType,
  PageTemplate,
  PageTemplateLite,
  UpdatePageTemplatePayload,
} from './types';

class TemplateAPIClient extends HttpClient {
  public getAllPageTemplates = <
    T extends PageType = PageType,
    S extends SourceType = SourceType,
  >(
    pageType: T,
    sourceType: S,
  ): Promise<PageTemplateLite<T, S>[]> => {
    return this.get<PageTemplateLite<T, S>[]>('pages/template/light', {
      params: {
        pageType,
        sourceType,
      },
    });
  };
  public getPageTemplate = <
    T extends PageType = PageType,
    S extends SourceType = SourceType,
  >(
    id: number,
  ): Promise<PageTemplate<T, S>> =>
    this.get<PageTemplate<T, S>>(`/pages/template/${id}`).then((response) => ({
      ...response,
      tiptapJson: transformJSONFromOld(response.tiptapJson),
    }));
  public createPageTemplate = <
    T extends PageType = PageType,
    S extends SourceType = SourceType,
  >(
    payload: CreatePageTemplatePayload<T, S>,
  ): Promise<PageTemplate<T, S>> => this.post('/pages/template', payload);

  public updatePageTemplate = <
    T extends PageType = PageType,
    S extends SourceType = SourceType,
  >(
    payload: UpdatePageTemplatePayload<T, S>,
  ): Promise<PageTemplate<T, S>> =>
    this.put(`/pages/template/${payload.templateId}`, payload);

  public deletePageTemplate = (templateId: number): Promise<void> =>
    this.delete<void>(`pages/template/${templateId}`);

  public updatePageTemplateOrder = (
    templateId: number,
    order: number,
  ): Promise<void> =>
    this.put<void, { templateId: number; order: number }>(
      'pages/template/reorder',
      {
        templateId,
        order,
      },
    );

  public updatePageTemplateStatus = (
    templateId: number,
    status: boolean,
  ): Promise<void> =>
    this.put<void, { status: boolean }>(
      `pages/template/${templateId}/toggle-status?newStatus=${status}`,
    );

  public getPageLevelTemplatePreview = async (
    meetingId: number,
    pageType: PageType,
    payload?: object,
    signal?: AbortSignal,
  ): Promise<any> => {
    try {
      return await this.post<any, { tiptapJson: object | undefined }>(
        `pages/preview/${meetingId}?pageType=${pageType}&streaming=true&documentStructureOnly=true`,
        {
          tiptapJson: payload,
        },
        { signal },
      );
    } catch (e) {
      throw new Error('Failed to fetch page preview', { cause: e });
    }
  };

  public getSectionLevelPreview = async (
    meetingId: number,
    pageType: PageType,
    payload: object,
    streaming: boolean,
  ): Promise<any> => {
    try {
      return await this.post(
        `pages/preview/section/${meetingId}?pageType=${pageType}&streaming=${streaming}`,
        { originalContent: payload },
      );
    } catch (e) {
      throw new Error('Failed to fetch section preview', { cause: e });
    }
  };
}

export const PageTemplateAPI = new TemplateAPIClient();
